import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../DesignSystem/button";
import AddPortForm from "./form";
import AddPortService from "../../services/addPort.service";
import DashboardService from "../../services/dashboard.service";
import { Regex, ErrorMessages, DefaultRxTxValue, MAX_NO_PORTS_ALLOWED } from "../../utils/appConstants";
import { AuthRoutes } from "../../enums";
import ErrorInfoIcon from "../../assets/images/error-info-icon.svg";
import { ErrorCodeMessageMapping, ErrorCodesMapping, SuccessMessage, ErrorMessages as Errors } from "../../utils/apiConstants";
import SuccessInfoIcon from "../../assets/images/success-toast-checkmark-icon.svg";
import { useIsMounted } from "../../utils/customHooks";
import { useGroup } from "../ManagePorts/GroupOperations/useGroup";
import Dropdown from "../DesignSystem/dropdown_autocomplete";

const AddPort: React.FC = () => {
  const { state } = useLocation() as any;
  const navigate = useNavigate();
  // @ts-ignore
  const [selectedPortType, setSelectedPortType] = useState<number>(1);
  const [selectedNetworkElement, setSelectedNetworkElement] = useState<any>(state || {});
  const [networkElementInputValue, setNetworkElementInputValue] = useState<string>((state && state.ne_name) || "");
  const [portDescription, setPortDescription] = useState<string>("");
  const [noOfPortsError, setNoOfPortsError] = useState<string>("");
  const [noOfPortsApiError, setNoOfPortsApiError] = useState<string>("");
  const [noOfPortsToAdd, setNoOfPortsToAdd] = useState<number | string>(1);
  const [networkElements, setNetworkElements] = useState<any[]>([]);
  const [portTypes, setPortTypes] = useState<any[]>([]);
  const [portsInfo, setPortsInfo] = useState<any[]>([]);
  const [addPortsLoading, setAddPortsLoading] = useState<boolean>(false);
  const [verifyPorts, setVerifyPorts] = useState<boolean>(false);
  const [portPrefix, setPortPrefix] = useState<string>("Port");
  const isMounted = useIsMounted();

  const { GroupDrawerProps, GroupDropdownProps } = useGroup(selectedNetworkElement.id, { isMounted, isBandwidth: false, lowerDrawer: false }, null, null, {
    DefaultGroup: selectedNetworkElement ? selectedNetworkElement.ne_name : null,
  });

  const resetPortInfo = useCallback(
    (portPrefix: string, noPorts: string | number, selectedPortType: any, ensureValid: boolean = false) => {
      const tempPortsInfo = [];
      let count = 0;

      for (let i = 1; i <= Number(noPorts); i++) {
        let portName: string = "";
        do {
          count++;
          portName = portPrefix ? [portPrefix, count].join(" ") : count.toString();
        } while (ensureValid && !GroupDropdownProps.IsPortNameValid(portName) && count < 272);
        // If ensure valid is true, make sure the port name is valid

        tempPortsInfo.push({
          id: i,
          portName,
          rx: DefaultRxTxValue,
          tx: DefaultRxTxValue,
          portTypeId: selectedPortType,
        });
      }
      return tempPortsInfo;
    },
    [GroupDropdownProps.IsPortNameValid]
  );

  const fetchNetworkElementsAndPortTypes = useCallback(() => {
    AddPortService.getNetworkElements()
      .then((response: any) => {
        if (!isMounted) return;
        setNetworkElements(response.data || []);
        AddPortService.getPortTypes()
          .then((responsePortTypes: any) => {
            if (!isMounted) return;
            setPortTypes(responsePortTypes.data || []);
            if (responsePortTypes.data.length) {
              setSelectedPortType(responsePortTypes.data[0]?.id ?? 1);
            }
          })
          .catch(() => {
            if (!isMounted) return;
            toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
            setPortTypes([]);
          });
      })
      .catch(() => {
        if (!isMounted) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setNetworkElements([]);
      });
  }, [setNetworkElements, setPortTypes, state, setNetworkElementInputValue, portPrefix]);

  useEffect(() => {
    fetchNetworkElementsAndPortTypes();
    window.history.replaceState({}, document.title);
  }, []);

  useEffect(() => {
    setPortsInfo(resetPortInfo(portPrefix, noOfPortsToAdd, selectedPortType, true));
  }, [resetPortInfo]);

  const handleNetworkElementChange = useCallback(
    (value: any) => {
      const neName = networkElements.find((ne) => ne.ne_name === value) || "";
      setSelectedNetworkElement(neName);
      setNetworkElementInputValue(neName.ne_name);
      GroupDropdownProps.reset();

      setPortsInfo(resetPortInfo(portPrefix, noOfPortsToAdd, selectedPortType, true));
    },
    [selectedPortType, resetPortInfo, networkElements, portPrefix, portTypes, noOfPortsToAdd, setNetworkElementInputValue]
  );

  const handleNEInputChange = useCallback(
    (inputValue: string) => {
      setNetworkElementInputValue(inputValue);
    },
    [setNetworkElementInputValue]
  );

  const handlePortDescriptionChange = useCallback(
    (e: any) => {
      setPortDescription(e.target.value || "");
    },
    [setPortDescription]
  );
  const handlePortPrefixChange = useCallback(
    (e: any) => {
      setPortPrefix(e.target.value || "");
      setPortsInfo(resetPortInfo(e.target.value.trim(), noOfPortsToAdd, selectedPortType));
    },
    [setPortPrefix, noOfPortsToAdd, resetPortInfo, selectedPortType]
  );

  const handleNoOfPortsChange = useCallback(
    (e: any) => {
      if (e.target.value.trim() === "") {
        setNoOfPortsToAdd("");
        setNoOfPortsError(ErrorMessages.addPort.noOfPortsRequired);
      } else if (parseInt(e.target.value.trim()) === 0) {
        setNoOfPortsToAdd(e.target.value.trim());
        setNoOfPortsError(ErrorMessages.addPort.noOfPortsMinLimit);
      } else if (Regex.numbers.test(e.target.value.trim())) {
        if (parseInt(e.target.value.trim()) > MAX_NO_PORTS_ALLOWED) {
          setNoOfPortsToAdd(parseInt(e.target.value.trim()));
          setNoOfPortsError(ErrorMessages.addPort.noOfPortsMaxLimit);
          return;
        }
        setNoOfPortsToAdd(parseInt(e.target.value.trim()));
        setNoOfPortsError("");

        setPortsInfo(resetPortInfo(portPrefix, e.target.value.trim(), selectedPortType, true));
      }
    },
    [selectedPortType, setNoOfPortsToAdd, resetPortInfo, setNoOfPortsError, setPortsInfo, DefaultRxTxValue, selectedNetworkElement, portTypes, portPrefix]
  );

  const handlePortTypeChange = useCallback(
    (portTypeId: number) => {
      setSelectedPortType(portTypeId);
      const tempPortsInfo = portsInfo.map((portInfo) => {
        return {
          ...portInfo,
          portTypeId,
        };
      });
      setPortsInfo(tempPortsInfo);
    },
    [portsInfo, setPortsInfo]
  );

  const handlePortNameChange = useCallback(
    (portTempId: number, portName: string) => {
      const tempPortsInfo = portsInfo.map((portInfo) => {
        if (portInfo.id === portTempId) {
          return {
            ...portInfo,
            portName,
          };
        }
        return portInfo;
      });
      setPortsInfo(tempPortsInfo);
    },
    [portsInfo, setPortsInfo]
  );

  const handleAddPortsClick = useCallback(() => {
    setAddPortsLoading(true);
    const payload = {
      neId: Number(selectedNetworkElement.id),
      nodeId: Number(selectedNetworkElement.node_id),
      groupId: Number(GroupDropdownProps.groupId),
      groupName: String(GroupDropdownProps.groupName), // TODO: add API for this
      portDescription: "",
      reqVerify: verifyPorts,
      portDetails: portsInfo.map((portInfo) => {
        return {
          ...portInfo,
          portName: portInfo.portName.trim(),
        };
      }),
    };

    AddPortService.addPorts(payload)
      .then(() => {
        if (!isMounted) return;
        setAddPortsLoading(false);
        setNoOfPortsApiError("");
        toast.success(SuccessMessage.addPort, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
        navigate(AuthRoutes.managePorts);
      })
      .catch((e: any) => {
        if (!isMounted) return;
        setAddPortsLoading(false);
        if (e.errorCode === ErrorCodesMapping[1006]) {
          setNoOfPortsApiError(ErrorCodeMessageMapping[1006]);
        } else if (e.errorCode === ErrorCodesMapping[1007]) {
          toast.error(ErrorCodeMessageMapping[1007], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          setNoOfPortsApiError("");
        } else {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        }
      });
  }, [
    selectedNetworkElement,
    portDescription,
    portsInfo,
    verifyPorts,
    setAddPortsLoading,
    setNoOfPortsApiError,
    GroupDropdownProps.groupId,
    GroupDropdownProps.groupName,
  ]);

  const addPortDisabled = !!(
    noOfPortsError ||
    !GroupDropdownProps.groupName ||
    !GroupDropdownProps.groupName.trim() ||
    portsInfo.find((portInfo) => !portInfo.portName.trim() || !GroupDropdownProps.IsPortNameValid(portInfo.portName.trim()))
  );

  const handleKeypress = useCallback(
    (e) => {
      if (e.key === "Enter" && !addPortDisabled) {
        handleAddPortsClick();
      }
    },
    [handleAddPortsClick, addPortDisabled]
  );

  const handleVerifyChange = useCallback(() => {
    setVerifyPorts(!verifyPorts);
  }, [setVerifyPorts, verifyPorts]);

  return (
    <div className="form-root">
      <div className="form-main">
        <AddPortForm
          verifyPorts={verifyPorts}
          networkElementOptions={networkElements}
          portTypeOptions={portTypes}
          selectedNetworkElement={selectedNetworkElement}
          portDescription={portDescription}
          noOfPortsToAdd={noOfPortsToAdd}
          noOfPortsError={noOfPortsApiError || noOfPortsError}
          portsInfo={portsInfo}
          handleVerifyChange={handleVerifyChange}
          onKeypress={handleKeypress}
          onNetworkElementChange={handleNetworkElementChange}
          onPortDescriptionChange={handlePortDescriptionChange}
          onNoOfPortsChange={handleNoOfPortsChange}
          selectedPortType={selectedPortType}
          onPortTypeChange={handlePortTypeChange}
          onPortNameChange={handlePortNameChange}
          inputValue={networkElementInputValue}
          onInputChange={handleNEInputChange}
          GroupDropdownProps={GroupDropdownProps}
          portPrefix={portPrefix}
          handlePortPrefixChange={handlePortPrefixChange}
        />
      </div>
      <div className="btn-root">
        <Link to={AuthRoutes.managePorts} className="cancel-btn-link">
          <Button variant="greyed" className="btn cancel-btn" node="Cancel" />
        </Link>
        <Button variant="primary" className="btn" node="Add Port" disabled={addPortDisabled} loading={addPortsLoading} onClick={handleAddPortsClick} />
      </div>
      {GroupDrawerProps.drawerComponent}
    </div>
  );
};

export default AddPort;
